import {useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useMutation, useQuery} from '@apollo/client';
import {
  Alert,
  Button,
  Form,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {
  EditOutlined,
  FileTextOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';
import DBModal from 'components/modal/modal';
import {fieldRules} from 'utils/helper';
import {CREATE_AI_SKILL, GET_ALL_AI_SKILLS, UPDATE_AI_SKILL} from './resolvers';
import {
  formatScenarioTreeForDisplay,
  normalizeScenarioTree,
} from './skillScenarioFormat';
import {
  SKILL_DOMAIN_OPTIONS,
  SKILL_NODE_OPTIONS,
  SKILL_STATUS_OPTIONS,
  SKILL_STATUS_TAG_COLOR,
  SKILL_TREE_OPTIONS,
} from './skillConstants';
import {formatUserDisplayName} from './userDisplay';
import styles from './aiAutomation.module.scss';

const EMPTY_FILTERS = {status: undefined, domain: undefined};
const CLOSED_MODAL = {open: false, mode: 'create', record: null};

const buildFilterVariable = filters => {
  const filter = {};
  if (filters.status) filter.status = filters.status;
  if (filters.domain) filter.domain = filters.domain;
  return Object.keys(filter).length ? filter : undefined;
};

const formatDate = value =>
  value ? moment(value).format('DD MMM YYYY, hh:mm A') : '-';

const truncate = (value, max = 28) => {
  if (!value) return '-';
  return value.length > max ? `${value.slice(0, max)}…` : value;
};

const SkillForm = ({form, mode, onFinish, submitting}) => {
  const isEdit = mode === 'edit';
  return (
    <div className={styles.formRoot}>
      <h2>{isEdit ? 'Update Skill' : 'Create Skill'}</h2>
      <Form form={form} onFinish={onFinish} layout="vertical">
        <Form.Item
          name="node"
          label="Category"
          tooltip="Broad topic this skill belongs to (e.g. Deposit, Withdrawal, etc.)"
          rules={[fieldRules('Category')]}>
          <Select
            placeholder="Select category"
            options={SKILL_NODE_OPTIONS}
            showSearch
            allowClear
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item
          name="tree"
          label="Scenario"
          tooltip="Specific situation or sub-topic within the category (e.g. Pending Deposit, Failed Deposit, etc.)"
          rules={[fieldRules('Scenario')]}>
          <Select
            placeholder="Select scenario"
            options={SKILL_TREE_OPTIONS}
            showSearch
            allowClear
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item
          name="domain"
          label="Team"
          tooltip="Team that owns this skill"
          rules={[fieldRules('Team')]}>
          <Select placeholder="Select team" options={SKILL_DOMAIN_OPTIONS} />
        </Form.Item>
        <Form.Item name="status" label="Status" rules={[fieldRules('Status')]}>
          <Select placeholder="Select status" options={SKILL_STATUS_OPTIONS} />
        </Form.Item>
        <div className={styles.formActions}>
          <Button type="primary" htmlType="submit" loading={submitting}>
            {isEdit ? 'Save Changes' : 'Create Skill'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export const SkillsManagement = () => {
  const loggedInUserId = useSelector(state => state?.auth?.userData?.user?.id);
  const navigate = useNavigate();

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [modal, setModal] = useState(CLOSED_MODAL);
  const [form] = Form.useForm();

  const filterVariable = useMemo(() => buildFilterVariable(filters), [filters]);

  const {data, loading, refetch, error} = useQuery(GET_ALL_AI_SKILLS, {
    variables: {filter: filterVariable},
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const response = data?.getSkills;
  const isError = response?.__typename === 'Error';
  const rows =
    response?.__typename === 'ResponseWithSkills'
      ? (response.data || []).filter(Boolean)
      : [];
  const errorMessage = isError ? response?.message : error?.message;

  const [createSkill, {loading: creating}] = useMutation(CREATE_AI_SKILL, {
    errorPolicy: 'all',
    onError: err => toast.error(err.message),
  });

  const [updateSkill, {loading: updating}] = useMutation(UPDATE_AI_SKILL, {
    errorPolicy: 'all',
    onError: err => toast.error(err.message),
  });

  const submitting = creating || updating;

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({...prev, [key]: value || undefined}));
  };

  const handleResetFilters = () => setFilters(EMPTY_FILTERS);

  const openCreate = () => {
    form.resetFields();
    setModal({open: true, mode: 'create', record: null});
  };

  const openEdit = record => {
    form.setFieldsValue({
      node: record?.node,
      tree: record?.tree,
      domain: record?.domain,
      status: record?.status,
    });
    setModal({open: true, mode: 'edit', record});
  };

  const closeModal = () => {
    setModal(CLOSED_MODAL);
    form.resetFields();
  };

  const performSubmit = async values => {
    const tree = normalizeScenarioTree(values.tree);

    if (modal.mode === 'edit') {
      const input = {
        id: modal.record?.id,
        tree,
        node: values.node,
        status: values.status,
        domain: values.domain,
        ...(loggedInUserId ? {updatedBy: String(loggedInUserId)} : {}),
      };
      const {data: result} = await updateSkill({variables: {input}});
      const payload = result?.updateSkill;
      if (!payload) return;
      if (payload.__typename === 'Error') {
        toast.error(payload.message || 'Failed to update skill');
        return;
      }
      toast.success(payload.message || 'Skill updated successfully');
      closeModal();
      refetch();
      return;
    }

    const input = {
      tree,
      node: values.node,
      status: values.status,
      domain: values.domain,
      ...(loggedInUserId ? {createdBy: String(loggedInUserId)} : {}),
    };
    const {data: result} = await createSkill({variables: {input}});
    const payload = result?.createSkill;
    if (!payload) return;
    if (payload.__typename === 'Error') {
      toast.error(payload.message || 'Failed to create skill');
      return;
    }
    toast.success(payload.message || 'Skill created successfully');
    closeModal();
    refetch();
  };

  const handleSubmit = values => {
    const isEdit = modal.mode === 'edit';
    Modal.confirm({
      title: isEdit ? 'Update skill?' : 'Create skill?',
      content: isEdit
        ? 'Are you sure you want to save changes to this skill?'
        : 'Are you sure you want to create this skill?',
      okText: isEdit ? 'Save Changes' : 'Create',
      cancelText: 'Cancel',
      onOk: () => performSubmit(values),
    });
  };

  const columns = [
    {
      title: 'Team',
      dataIndex: 'domain',
      key: 'domain',
      render: value => value || '-',
    },
    {
      title: 'Category',
      dataIndex: 'node',
      key: 'node',
      render: value => (
        <Tooltip title={value || ''}>
          <span>{truncate(value)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Scenario',
      dataIndex: 'tree',
      key: 'tree',
      render: value => {
        const display = formatScenarioTreeForDisplay(value);
        return (
          <Tooltip title={display || ''}>
            <span>{truncate(display || '-')}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: value =>
        value ? (
          <Tag color={SKILL_STATUS_TAG_COLOR[value] || 'blue'}>{value}</Tag>
        ) : (
          '-'
        ),
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: value => truncate(formatUserDisplayName(value), 16),
    },
    {
      title: 'Updated By',
      dataIndex: 'updatedBy',
      key: 'updatedBy',
      render: value => truncate(formatUserDisplayName(value), 16),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: formatDate,
    },
    {
      title: 'Last Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: formatDate,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}>
            Edit
          </Button>
          <Button
            type="link"
            icon={<FileTextOutlined />}
            onClick={() =>
              navigate(
                `/dashboard/ai-automation/skills/${record.id}/translations`,
                {
                  state: {
                    skill: {
                      id: record.id,
                      domain: record.domain,
                      node: record.node,
                      tree: record.tree,
                      status: record.status,
                    },
                  },
                },
              )
            }>
            Translations
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className={styles.skillsRoot}>
      <div className={styles.skillsToolbar}>
        <div className={styles.filters}>
          <Select
            className={styles.filterSelect}
            placeholder="Filter by status"
            value={filters.status}
            onChange={value => handleFilterChange('status', value)}
            options={SKILL_STATUS_OPTIONS}
            allowClear
          />
          <Select
            className={styles.filterSelect}
            placeholder="Filter by team"
            value={filters.domain}
            onChange={value => handleFilterChange('domain', value)}
            options={SKILL_DOMAIN_OPTIONS}
            allowClear
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={handleResetFilters}
            disabled={!filters.status && !filters.domain}>
            Reset
          </Button>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          New Skill
        </Button>
      </div>

      {errorMessage ? (
        <Alert
          type="error"
          showIcon
          message={errorMessage}
          className={styles.errorAlert}
        />
      ) : null}

      <Table
        className="antd-table"
        columns={columns}
        dataSource={rows}
        loading={loading}
        rowKey={(record, index) => record?.id || index}
        pagination={{pageSize: 10, showSizeChanger: true}}
      />

      <DBModal
        isOpen={modal.open}
        handleClose={closeModal}
        destroyOnClose
        content={
          <SkillForm
            form={form}
            mode={modal.mode}
            onFinish={handleSubmit}
            submitting={submitting}
          />
        }
      />
    </div>
  );
};
